import {
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { HttpClient } from "@angular/common/http";
import { NestedTreeControl } from "@angular/cdk/tree";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatDrawer } from "@angular/material/sidenav";
import { faFolder, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { ToastrService } from "ngx-toastr";
import { EMPTY, forkJoin, from, of, Subject, Subscription, switchMap, timer } from "rxjs";
import { catchError, delay, finalize, retry, takeUntil, timeout } from "rxjs/operators";
import { SpecialCompbaser } from "../compbaser";
import { IAppStatic } from "../../interfaces";
import { LocalStoreService, StoreSelectorsService } from "../../services";
import { timeout as timeoutDecorator } from "../../decorators/timeout";
import { HOST_DIR, isHostDevOrLocal, traverseTree } from "../../common";
import { TutorialsService } from "../../services/tutorials.service";

interface ITip {
    time: number;
    duration: number;
    link: string;
    name: string;
}

interface TreeData {
    name: string;
    iconname: string;
    url?: string;
    audio?: string;
    vtt?: string;
    videoid?: string;
    children?: TreeData[];
    tips?: Array<ITip>;
    lesson?: boolean;
    whiteLabel?: boolean;
    nextVideoId?: string;
}

interface VideoAPI {
    play(): void;

    pause(): void;

    currentTime: number;
    state: string;
    subscriptions?: {
        timeUpdate?: any;
        volumeChange?: any;
        muteChange?: any;
    };

    getDefaultMedia?(): any;
}

const getChildren = (node: TreeData) => of(node.children || []);

@Component({
    selector: "app-tutorials",
    templateUrl: "./tutorials.component.html",
    styleUrls: ["./tutorials.component.scss"],
})
export class TutorialsComponent extends SpecialCompbaser implements OnInit, OnDestroy {
    readonly faFolder = faFolder;
    readonly faFolderOpen = faFolderOpen;

    // Private properties with safe defaults
    private appSettings: IAppStatic | null = null;
    private destroy$ = new Subject<void>();
    private apiRetryAttempts = 0;
    private readonly maxApiRetryAttempts = 5;
    private initializationTimeout: any = null;
    private isDestroyed = false;

    // Public properties with safe defaults
    clickTimeout: any = null;
    lessonMode = false;
    selectedTabIndex = 0;
    lessonVideoId: string | null = null;
    tutorials: TreeData | null = null;
    videoSource: string | null = null;
    audioSource: string | null = null;
    vttSource: string | null = null;
    showTips = false;
    normalMode = true;
    selectedTreeData: TreeData | null = null;
    videoUrl = "";
    videoSubscriber: Subscription | null = null;
    tips: Array<ITip> = [];
    toggleIcon = true;
    treeControl = new NestedTreeControl(getChildren);
    dataSource = new MatTreeNestedDataSource<TreeData>();
    searchVideos: TreeData[] = [];
    vgAPI: VideoAPI | null = null;
    showManual = false;
    filter = "";
    activeVideoId = "";
    isVideoEnded = false;
    maxAudioGap = 0.1;
    currentSubtitle = "";
    videoReady$: Subject<any> | null = null;
    audioReady$: Subject<any> | null = null;
    vttReady$: Subject<any> | null = null;
    videoInit: Subscription | null = null;
    watchedVideoLinks: Record<string, any> = {};
    private currentVolume = 1.0;
    private isMuted = false;

    @ViewChild("copyUrlModal", { read: TemplateRef }) copyUrlModal: TemplateRef<any> | null = null;
    @ViewChild("sidenav") sidenav: MatDrawer | null = null;
    @ViewChild("videoElement") videoElement: ElementRef<HTMLVideoElement> | null = null;
    @ViewChild("audioPlayer") audioPlayer: ElementRef<HTMLAudioElement> | null = null;

    @Input()
    set setAutoPlayVideoId(i_autoPlayVideoId: string | null) {
        if (this.isDestroyed) return;

        try {
            console.log("videoId = " + i_autoPlayVideoId);

            if (i_autoPlayVideoId == null) {
                this.loadTutorialsData();
            } else {
                this.activeVideoId = i_autoPlayVideoId;
                this.showManual = true;
                this.startVideo();

                // Auto-hide manual after 5 seconds
                setTimeout(() => {
                    if (!this.isDestroyed) {
                        this.showManual = false;
                    }
                }, 5000);
            }
        } catch (error) {
            console.error("Error in setAutoPlayVideoId:", error);
            this.handleError("Failed to set auto-play video", error);
        }
    }

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) data: any,
        @Optional() private dialogRef: MatDialogRef<any> | null,
        private route: ActivatedRoute,
        private _http: HttpClient,
        private translocoService: TranslocoService,
        private storeSelector: StoreSelectorsService,
        private localStoreService: LocalStoreService,
        private tutorialsService: TutorialsService,
        private toastr: ToastrService,
        dialog: MatDialog,
    ) {
        super(dialog);

        try {
            this.initializeRouteSubscription();
            this.initializeAppSettingsSubscription(data);
        } catch (error) {
            console.error("Error in constructor:", error);
            this.handleError("Component initialization failed", error);
        }
    }

    private initializeRouteSubscription(): void {
        try {
            this.cod(
                this.route.paramMap.pipe(
                    takeUntil(this.destroy$),
                    catchError(error => {
                        console.error("Error in route paramMap:", error);
                        return of(null);
                    }),
                ).subscribe((routeParams) => {
                    if (routeParams && !this.isDestroyed) {
                        const videoId = routeParams.get("video_id");
                        if (videoId) {
                            this.activeVideoId = videoId;
                        }
                    }
                }),
            );
        } catch (error) {
            console.error("Error initializing route subscription:", error);
        }
    }

    private initializeAppSettingsSubscription(data: any): void {
        try {
            this.cod(
                this.storeSelector.getAppSettings().pipe(
                    takeUntil(this.destroy$),
                    catchError(error => {
                        console.error("Error getting app settings:", error);
                        return of(null);
                    }),
                ).subscribe((appSettings: IAppStatic | null) => {
                    if (appSettings && !this.isDestroyed) {
                        this.appSettings = appSettings;
                        this.initializeStorageData(data);
                    }
                }),
            );
        } catch (error) {
            console.error("Error initializing app settings subscription:", error);
        }
    }

    private loadTutorialsData(): void {
        if (this.isDestroyed) return;

        try {
            this.tutorialsService.getData()
                .pipe(
                    catchError(error => {
                        console.error("Error loading tutorials data:", error);
                        if (isHostDevOrLocal()) this.toastr.error("Failed to load tutorials data");
                        return of(null);
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe(data => {
                    if (data && !this.isDestroyed) {
                        this.tutorials = data;
                        this.selectTreeNode();
                        this.selectedTabIndex = 1;
                    }
                });
        } catch (error) {
            console.error("Error in loadTutorialsData:", error);
            this.handleError("Failed to load tutorials", error);
        }
    }

    private initializeStorageData(data: any): void {
        try {
            // Initialize watchedVideoLinks safely
            this.watchedVideoLinks = this.localStoreService.getStorageValConfig("watchedVideoLinks") || {};
            this.lessonVideoId = this.localStoreService.getStorageValConfig("lessonVideoId") || null;

            if (data && !this.isDestroyed) {
                this.lessonMode = data.lessonMode || false;
                let videoId = data.videoLink;

                if (this.lessonMode) {
                    if (this.lessonVideoId == null) {
                        this.lessonVideoId = data.videoLink;
                    } else {
                        videoId = this.lessonVideoId;
                    }
                }

                this.normalMode = false;
                this.activeVideoId = videoId || "";
                this.startVideo();
            }
        } catch (error) {
            console.error("Error initializing storage data:", error);
            this.watchedVideoLinks = {};
            this.handleError("Failed to initialize storage", error);
        }
    }

    ngOnInit(): void {
        // Component initialization complete
        console.log("TutorialsComponent initialized");
    }

    override ngOnDestroy(): void {
        this.isDestroyed = true;

        try {
            // Clear timeouts
            if (this.clickTimeout) {
                clearTimeout(this.clickTimeout);
                this.clickTimeout = null;
            }

            if (this.initializationTimeout) {
                clearTimeout(this.initializationTimeout);
                this.initializationTimeout = null;
            }

            // Complete destroy subject
            this.destroy$.next();
            this.destroy$.complete();

            // Clean up video resources
            this.clearVideo();

            // Unsubscribe from video events
            if (this.videoSubscriber) {
                this.videoSubscriber.unsubscribe();
                this.videoSubscriber = null;
            }

            // Save progress before destroying
            this.saveStorageProgress();

            // Reset API reference
            this.vgAPI = null;
        } catch (error) {
            console.error("Error in ngOnDestroy:", error);
        }
    }

    onTabChange(event: any): void {
        if (this.isDestroyed) return;

        try {
            if (event?.index === 0) {
                this.lessonMode = true;
                this.updateLessonsMode();
            } else if (event?.index === 1) {
                this.lessonMode = false;
            }
        } catch (error) {
            console.error("Error in onTabChange:", error);
            this.handleError("Tab change failed", error);
        }
    }

    updateLessonsMode(): void {
        if (this.isDestroyed) return;

        try {
            this.filter = "";
            this.searchVideos = [];
            let i = 1;

            if (this.dataSource?.data) {
                traverseTree(this.dataSource.data, (x, y, n) => {
                    if (n?.videoid && n?.name && n?.lesson &&
                        this.searchVideos.findIndex((v) => v?.videoid === n.videoid) === -1) {
                        try {
                            const node = JSON.parse(JSON.stringify(n));
                            node.name = "Lesson " + i + ": " + node.name;
                            this.searchVideos.push(node);
                            i++;
                        } catch (parseError) {
                            console.error("Error parsing node:", parseError);
                        }
                    }
                });
            }
        } catch (error) {
            console.error("Error in updateLessonsMode:", error);
            this.handleError("Failed to update lessons mode", error);
        }
    }

    searchTutorial(): void {
        if (this.isDestroyed) return;

        try {
            if (!this.filter || this.filter.length < 2 || !this.dataSource?.data) return;

            this.searchVideos = [];
            const filterLower = this.filter.toLowerCase();

            traverseTree(this.dataSource.data, (x, y, n) => {
                if (n?.videoid && n?.name &&
                    n.name.toLowerCase().indexOf(filterLower) > -1 &&
                    this.searchVideos.findIndex((v) => v?.videoid === n.videoid) === -1) {
                    this.searchVideos.push(n);
                }
            });
        } catch (error) {
            console.error("Error in searchTutorial:", error);
            this.handleError("Search failed", error);
        }
    }

    selectTreeNode(): void {
        if (this.isDestroyed) return;

        try {
            if (this.tutorials) {
                traverseTree(this.tutorials, (x, y, n) => {
                    if (n?.videoid && n.videoid === this.activeVideoId) {
                        this.selectedTreeData = n;
                    }
                });

                this.dataSource.data = [this.tutorials];

                if (this.activeVideoId) {
                    this.expandNode(this.dataSource.data[0], this.activeVideoId);
                } else if (this.dataSource.data[0]) {
                    this.treeControl.expand(this.dataSource.data[0]);
                }
            }
        } catch (error) {
            console.error("Error in selectTreeNode:", error);
            this.handleError("Failed to select tree node", error);
        }
    }

    private dialogMode(i_data: any): void {
        if (this.isDestroyed) return;

        try {
            this.normalMode = false;
            setTimeout(() => {
                if (!this.isDestroyed) {
                    this.playClick();
                }
            }, 20);
        } catch (error) {
            console.error("Error in dialogMode:", error);
        }
    }

    exitDialogMode(): void {
        try {
            if (this.dialogRef) {
                this.dialogRef.close();
            }
        } catch (error) {
            console.error("Error in exitDialogMode:", error);
        }
    }

    onMinimized(): void {
        try {
            if (this.sidenav) {
                this.sidenav.close();
            }
        } catch (error) {
            console.error("Error in onMinimized:", error);
        }
    }

    onMaximized(): void {
        try {
            if (this.sidenav) {
                this.sidenav.open();
            }
        } catch (error) {
            console.error("Error in onMaximized:", error);
        }
    }

    private expandNode(data: TreeData, videoid: string): boolean {
        if (!data || !videoid) return false;

        try {
            if (data.videoid === videoid) {
                return true;
            }
            if (!data.children || !data.children.length) {
                return false;
            }
            for (const node of data.children) {
                if (this.expandNode(node, videoid)) {
                    this.treeControl.expand(data);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error("Error in expandNode:", error);
            return false;
        }
    }

    goFullScreen(): void {
        // Implementation for fullscreen functionality
        try {
            if (this.videoElement?.nativeElement) {
                const video = this.videoElement.nativeElement;
                if (video.requestFullscreen) {
                    video.requestFullscreen().catch(error => {
                        console.error("Error requesting fullscreen:", error);
                    });
                }
            }
        } catch (error) {
            console.error("Error in goFullScreen:", error);
        }
    }

    private isApiValid(api: any): boolean {
        if (!api) {
            return false;
        }

        try {
            // Check for essential properties/methods
            const requiredMethods = ["play", "pause"];
            const requiredProperties = ["currentTime"];

            // Check methods
            for (const method of requiredMethods) {
                if (typeof api[method] !== "function") {
                    console.warn(`API validation failed: missing method ${method}`);
                    return false;
                }
            }

            // Check properties
            for (const prop of requiredProperties) {
                if (!(prop in api)) {
                    console.warn(`API validation failed: missing property ${prop}`);
                    return false;
                }
            }

            return true;
        } catch (error) {
            console.error("Error during API validation:", error);
            return false;
        }
    }

    private waitForApiReady(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const maxAttempts = 15;
            let attempts = 0;

            const checkApi = () => {
                if (this.isDestroyed) {
                    reject(new Error("Component destroyed during API initialization"));
                    return;
                }

                attempts++;

                try {
                    // Check if API and required properties are available
                    if (this.vgAPI &&
                        this.vgAPI.subscriptions &&
                        this.vgAPI.subscriptions.timeUpdate &&
                        typeof this.vgAPI.currentTime !== "undefined") {
                        console.log("API is ready");
                        resolve(true);
                    } else if (attempts >= maxAttempts) {
                        reject(new Error(`API not ready after ${maxAttempts} attempts`));
                    } else {
                        setTimeout(checkApi, 300);
                    }
                } catch (error) {
                    if (attempts >= maxAttempts) {
                        reject(error);
                    } else {
                        setTimeout(checkApi, 300);
                    }
                }
            };

            checkApi();
        });
    }

    private setupTimeUpdateSubscription(): void {
        if (this.isDestroyed || !this.vgAPI?.subscriptions?.timeUpdate) return;

        try {
            // Clean up existing subscription
            if (this.videoSubscriber) {
                this.videoSubscriber.unsubscribe();
                this.videoSubscriber = null;
            }

            this.videoSubscriber = this.vgAPI.subscriptions.timeUpdate
                .pipe(
                    catchError((error) => {
                        console.error("Error in timeUpdate subscription:", error);
                        return of(null);
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe({
                    next: () => {
                        if (!this.isDestroyed) {
                            try {
                                this.handleTimeUpdate();
                            } catch (error) {
                                console.error("Error handling time update:", error);
                            }
                        }
                    },
                    error: (error) => {
                        console.error("TimeUpdate subscription error:", error);
                        if (!this.isDestroyed) {
                            this.attemptReconnection();
                        }
                    },
                });
        } catch (error) {
            console.error("Error setting up time update subscription:", error);
        }
    }

    private handleTimeUpdate(): void {
        if (this.isDestroyed || !this.vgAPI || typeof this.vgAPI.currentTime === "undefined") {
            return;
        }

        try {
            if (this.selectedTreeData?.tips) {
                this.tips = [];

                this.selectedTreeData.tips.forEach((tip) => {
                    if (!tip || typeof tip.time !== "number" || typeof tip.duration !== "number") return;

                    try {
                        const currentTime = this.vgAPI!.currentTime;
                        if (currentTime > tip.time && currentTime < (tip.time + tip.duration)) {
                            this.tips.push(tip);
                        }
                    } catch (error) {
                        console.error("Error processing tip:", tip, error);
                    }
                });

                if (this.tips.length > 0) {
                    try {
                        this.tipOpenDialog();
                    } catch (error) {
                        console.error("Error opening tip dialog:", error);
                    }
                } else {
                    this.onCloseTip();
                }
            } else {
                this.onCloseTip();
            }
        } catch (error) {
            console.error("Error in handleTimeUpdate:", error);
        }
    }

    private attemptReconnection(): void {
        if (this.isDestroyed) return;

        try {
            console.log("Attempting to reconnect video API...");

            if (this.videoSubscriber) {
                this.videoSubscriber.unsubscribe();
                this.videoSubscriber = null;
            }

            timer(2000).pipe(
                takeUntil(this.destroy$),
            ).subscribe(() => {
                if (!this.isDestroyed && this.vgAPI) {
                    this.setupTimeUpdateSubscription();
                }
            });
        } catch (error) {
            console.error("Error in attemptReconnection:", error);
        }
    }

    onCloseTip(): void {
        try {
            this.tips = [];
            this.showTips = false;
        } catch (error) {
            console.error("Error in onCloseTip:", error);
        }
    }

    tipOpenDialog(): void {
        try {
            this.showTips = true;
        } catch (error) {
            console.error("Error in tipOpenDialog:", error);
        }
    }

    onOpenLink(i_url?: string): void {
        try {
            const url = i_url ?? this.videoUrl;
            if (url) {
                window.open(url, "_blank");
            }
        } catch (error) {
            console.error("Error opening link:", error);
        }
    }

    openModal(node: TreeData): void {
        try {
            if (this.appSettings && node?.videoid && this.copyUrlModal) {
                this.videoUrl = `${this.appSettings.protocol}${this.appSettings.masterServer}/${HOST_DIR}/shopping-cart/helpdesk/${node.videoid}`;
                this.dialog.open(this.copyUrlModal);
            }
        } catch (error) {
            console.error("Error opening modal:", error);
        }
    }

    copyText(): void {
        try {
            const message = this.translocoService.translate("toast.copiedToClip", {}, this.translocoService.getActiveLang());
            this.toastr.info(message);
        } catch (error) {
            console.error("Error copying text:", error);
        }
    }

    clickedLeaf(e: TreeData): void {
        if (this.isDestroyed || !e?.videoid) return;

        try {
            // Clear existing timeout
            if (this.clickTimeout) {
                clearTimeout(this.clickTimeout);
                this.clickTimeout = null;
            }

            this.clickTimeout = setTimeout(() => {
                if (this.isDestroyed) return;

                this.clickTimeout = null;
                console.log("Single click executed");

                this.activeVideoId = e.videoid!;
                if (this.lessonMode) {
                    this.lessonVideoId = this.activeVideoId;
                }

                if (this.watchedVideoLinks && this.watchedVideoLinks[this.activeVideoId] == 100) {
                    this.watchedVideoLinks[this.activeVideoId] = 0;
                }

                this.saveStorage();

                this.selectedTreeData = e;
                this.showManual = false;
                this.videoSource = null;
                this.audioSource = null;
                this.vttSource = null;
                this.currentSubtitle = "";
                this.startVideo();
            }, 300);
        } catch (error) {
            console.error("Error in clickedLeaf:", error);
        }
    }

    hasChild(_: number, node: TreeData): boolean {
        try {
            return node?.children != null && node.children.length > 0;
        } catch (error) {
            console.error("Error in hasChild:", error);
            return false;
        }
    }

    toggleVideoList(): void {
        try {
            if (this.sidenav) {
                this.sidenav.toggle();
            }
        } catch (error) {
            console.error("Error toggling video list:", error);
        }
    }

    toggleVideoPane(): void {
        try {
            this.toggleIcon = !this.toggleIcon;
        } catch (error) {
            console.error("Error toggling video pane:", error);
        }
    }

    @timeoutDecorator()
    startVideo(): void {
        if (this.isDestroyed) return;

        try {
            console.log("startVideo");
            this.clearVideo();

            if (!this.tutorialsService) {
                console.error("TutorialsService not available");
                return;
            }

            this.videoInit = this.tutorialsService.getData().pipe(
                timeout(30000), // 30 second timeout
                switchMap(data => {
                    if (this.isDestroyed) return EMPTY;

                    this.tutorials = data;
                    this.selectTreeNode();
                    if (!this.lessonMode) {
                        this.selectedTabIndex = 1;
                    }
                    this.updateLessonsMode();
                    return of("ready");
                }),
                delay(500),
                switchMap(status => {
                    if (this.isDestroyed) return EMPTY;

                    if (this.selectedTreeData?.url != null) {
                        this.videoSource = this.selectedTreeData.url;
                        this.audioSource = this.selectedTreeData.audio || null;
                        this.vttSource = this.selectedTreeData.vtt || null;
                    } else if (this.selectedTreeData?.videoid) {
                        const videoId = this.selectedTreeData.videoid;
                        this.videoSource = (`https://mstutorials.s3.us-west-2.amazonaws.com/${videoId}/video.mp4`).toLowerCase();
                        this.audioSource = (`https://mstutorials.s3.us-west-2.amazonaws.com/${videoId}/audio.mp3`).toLowerCase();
                        this.vttSource = (`https://mstutorials.s3.us-west-2.amazonaws.com/${videoId}/subtitle_en.vtt`).toLowerCase();
                    }

                    // Create observables for media loading
                    this.videoReady$ = new Subject<any>();
                    const obsList = [this.videoReady$];

                    if (this.audioSource != null) {
                        this.audioReady$ = new Subject<any>();
                        obsList.push(this.audioReady$);
                    }

                    if (this.vttSource != null) {
                        this.vttReady$ = new Subject<any>();
                        obsList.push(this.vttReady$);
                    }

                    return forkJoin(obsList);
                }),
                catchError(error => {
                    console.error("Error in startVideo:", error);
                    if (isHostDevOrLocal()) this.toastr.error("Error loading video. Please try again.");
                    return of(null);
                }),
                takeUntil(this.destroy$),
                finalize(() => {
                    // Clean up initialization state
                    this.videoInit = null;
                }),
            ).subscribe(result => {
                if (result && !this.isDestroyed) {
                    try {
                        // Clean up subjects
                        this.cleanupSubjects();

                        // Start video playback
                        if (this.videoElement?.nativeElement) {
                            this.videoElement.nativeElement.play().catch(error => {
                                console.error("Error playing video:", error);
                            });
                        }

                        // Set audio volume
                        if (this.audioPlayer?.nativeElement) {
                            this.audioPlayer.nativeElement.volume = this.currentVolume;
                            this.audioPlayer.nativeElement.muted = this.isMuted;
                        }

                        this.maxAudioGap = 0.1;
                    } catch (error) {
                        console.error("Error in video initialization completion:", error);
                    }
                }
            });
        } catch (error) {
            console.error("Error in startVideo:", error);
            this.handleError("Failed to start video", error);
        }
    }

    private cleanupSubjects(): void {
        try {
            if (this.videoReady$) {
                this.videoReady$.complete();
                this.videoReady$ = null;
            }

            if (this.audioReady$) {
                this.audioReady$.complete();
                this.audioReady$ = null;
            }

            if (this.vttReady$) {
                this.vttReady$.complete();
                this.vttReady$ = null;
            }
        } catch (error) {
            console.error("Error cleaning up subjects:", error);
        }
    }

    onVideoCreated(): void {
        if (this.isDestroyed) return;

        try {
            const video: HTMLVideoElement | null = this.videoElement?.nativeElement || null;
            if (video && this.watchedVideoLinks && this.watchedVideoLinks[this.activeVideoId] != null) {
                let percentage = Number(this.watchedVideoLinks[this.activeVideoId]);
                if (isNaN(percentage)) {
                    percentage = 0;
                }
                video.currentTime = Math.max(0, (percentage / 100) * video.duration - 5);
            }

            if (video?.textTracks?.[0]) {
                const track = video.textTracks[0];
                track.mode = "hidden";
            }
        } catch (error) {
            console.error("Error in onVideoCreated:", error);
        }
    }

    onVideoCanPlay(): void {
        if (this.isDestroyed) return;

        try {
            if (this.videoReady$) {
                this.videoReady$.next("Video is ready");
                this.videoReady$.complete();
                this.videoReady$ = null;
            }
        } catch (error) {
            console.error("Error in onVideoCanPlay:", error);
        }
    }

    onAudioCanPlay(): void {
        if (this.isDestroyed) return;

        try {
            if (this.audioReady$) {
                this.audioReady$.next("Audio is ready");
                this.audioReady$.complete();
                this.audioReady$ = null;
            }
        } catch (error) {
            console.error("Error in onAudioCanPlay:", error);
        }
    }

    onVttCanPlay(): void {
        if (this.isDestroyed) return;

        try {
            if (this.vttReady$) {
                this.vttReady$.next("vtt is ready");
                this.vttReady$.complete();
                this.vttReady$ = null;
            }
        } catch (error) {
            console.error("Error in onVttCanPlay:", error);
        }
    }

    onSubtitle(event: any): void {
        if (this.isDestroyed) return;

        try {
            const track = this.videoElement?.nativeElement?.textTracks?.[0];
            const activeCue = track?.activeCues?.[0];
            this.currentSubtitle = activeCue ? (activeCue as VTTCue).text : "";
        } catch (error) {
            console.error("Error in onSubtitle:", error);
            this.currentSubtitle = "";
        }
    }

    onVideoPlay(): void {
        if (this.isDestroyed) return;

        try {
            console.log("onVideoPlay");
        } catch (error) {
            console.error("Error in onVideoPlay:", error);
        }
    }

    onVideoEnded(): void {
        if (this.isDestroyed) return;

        try {
            this.saveStorage();
            this.currentSubtitle = "";
            if (this.selectedTreeData?.nextVideoId) {
                this.isVideoEnded = true;
            }
        } catch (error) {
            console.error("Error in onVideoEnded:", error);
        }
    }

    onCancel(): void {
        try {
            this.isVideoEnded = false;
        } catch (error) {
            console.error("Error in onCancel:", error);
        }
    }

    onPlayNext(): void {
        if (this.isDestroyed || !this.selectedTreeData?.nextVideoId) return;

        try {
            this.isVideoEnded = false;
            this.activeVideoId = this.selectedTreeData.nextVideoId;
            this.lessonVideoId = this.activeVideoId;

            if (this.watchedVideoLinks) {
                this.watchedVideoLinks[this.activeVideoId] = 0;
            }

            this.saveStorage();
            this.showManual = false;
            this.videoSource = null;
            this.audioSource = null;
            this.vttSource = null;
            this.currentSubtitle = "";
            this.startVideo();
        } catch (error) {
            console.error("Error in onPlayNext:", error);
        }
    }

    getProgress(videoid: string): number {
        if (!videoid) return 0;

        try {
            if (this.watchedVideoLinks == null) return 0;
            if (this.watchedVideoLinks[videoid] == null || isNaN(Number(this.watchedVideoLinks[videoid]))) {
                return 0;
            }
            return Number(this.watchedVideoLinks[videoid]);
        } catch (error) {
            console.error("Error getting progress:", error);
            return 0;
        }
    }

    updateProgress(): void {
        if (this.isDestroyed || !this.activeVideoId) return;

        try {
            if (this.watchedVideoLinks == null || !this.videoElement?.nativeElement) return;

            const video = this.videoElement.nativeElement;
            if (video.duration && !isNaN(video.duration) && video.duration > 0) {
                const percentage = (video.currentTime / video.duration) * 100;
                this.watchedVideoLinks[this.activeVideoId] = Number(percentage.toFixed(2));
            }
        } catch (error) {
            console.error("Error updating progress:", error);
        }
    }

    saveStorage(): void {
        try {
            if (this.watchedVideoLinks) {
                this.localStoreService.setStorageValConfig("watchedVideoLinks", this.watchedVideoLinks);
            }
            if (this.lessonMode && this.lessonVideoId) {
                this.localStoreService.setStorageValConfig("lessonVideoId", this.lessonVideoId);
            }
        } catch (error) {
            console.error("Error saving storage:", error);
        }
    }

    private saveStorageProgress(): void {
        try {
            if (this.watchedVideoLinks && this.activeVideoId) {
                if (this.watchedVideoLinks[this.activeVideoId] == 100) {
                    if (this.selectedTreeData?.nextVideoId != null) {
                        this.activeVideoId = this.selectedTreeData.nextVideoId;
                        this.lessonVideoId = this.activeVideoId;
                    }
                    this.watchedVideoLinks[this.activeVideoId] = 0;
                }
            }
            this.saveStorage();
        } catch (error) {
            console.error("Error saving storage progress:", error);
        }
    }

    clearVideo(): void {
        try {
            console.log("clearVideo");

            // Unsubscribe from video initialization
            if (this.videoInit) {
                this.videoInit.unsubscribe();
                this.videoInit = null;
            }

            // Clear video sources
            if (this.videoElement?.nativeElement) {
                this.videoSource = "";
            }

            if (this.audioPlayer?.nativeElement) {
                this.audioSource = "";
            }

            // Clean up subjects
            this.cleanupSubjects();

        } catch (error) {
            console.error("Error clearing video:", error);
        }
    }

    // Enhanced onPlayerReady method with comprehensive error handling
    onPlayerReady(api: any): void {
        if (this.isDestroyed) return;

        try {
            // Validate the API parameter before proceeding
            if (!api) {
                console.error("onPlayerReady called with null/undefined API");
                if (isHostDevOrLocal()) this.toastr.error("Video player failed to initialize");
                return;
            }

            console.log("onPlayerReady called, API type:", typeof api, api);

            this.vgAPI = api;
            this.apiRetryAttempts = 0;

            // Clean up existing subscription
            if (this.videoSubscriber) {
                this.videoSubscriber.unsubscribe();
                this.videoSubscriber = null;
            }

            // Enhanced API validation before proceeding
            if (!this.isApiValid(api)) {
                console.warn("API validation failed, attempting retry...");
                this.retryApiInitialization();
                return;
            }

            // Wait for API to be ready with enhanced retry logic
            from(this.waitForApiReady())
                .pipe(
                    retry({
                        count: this.maxApiRetryAttempts,
                        delay: (error, retryCount) => {
                            console.warn(`API not ready, retry attempt ${retryCount}:`, error);
                            return timer(Math.min(1000 * retryCount, 5000)); // Cap at 5 seconds
                        },
                    }),
                    timeout(30000), // 30 second total timeout
                    catchError((error) => {
                        console.error("Failed to initialize video API after retries:", error);
                        this.handleApiInitializationFailure(error);
                        return of(null);
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe({
                    next: (success) => {
                        if (success && this.isApiValid(this.vgAPI) && !this.isDestroyed) {
                            this.initializeVideoFeatures();
                        } else if (!this.isDestroyed) {
                            console.warn("API initialization reported success but API is not valid");
                            this.handleApiInitializationFailure(new Error("API validation failed after initialization"));
                        }
                    },
                    error: (error) => {
                        if (!this.isDestroyed) {
                            console.error("Unexpected error in onPlayerReady subscription:", error);
                            this.handleApiInitializationFailure(error);
                        }
                    },
                });

        } catch (error) {
            console.error("Critical error in onPlayerReady initialization:", error);
            this.handleApiInitializationFailure(error);
        }
    }

    /**
     * Initialize video features with error handling
     */
    private initializeVideoFeatures(): void {
        if (this.isDestroyed) return;

        try {
            console.log("Initializing video features...");

            // Validate API once more before setup
            if (!this.isApiValid(this.vgAPI)) {
                throw new Error("API became invalid before feature initialization");
            }

            this.setupTimeUpdateSubscription();
            this.setupVolumeSubscription();

            console.log("Video features initialized successfully");
        } catch (error) {
            console.error("Error initializing video features:", error);
            this.handleApiInitializationFailure(error);
        }
    }

    /**
     * Handle API initialization failures consistently
     */
    private handleApiInitializationFailure(error: any): void {
        if (this.isDestroyed) return;

        console.error("Video API initialization failed:", error);
        if (isHostDevOrLocal()) this.toastr.warning("Video player initialization failed. Please refresh the page or try again.");
        // Reset API state
        this.vgAPI = null;
        this.apiRetryAttempts = 0;

        // Clean up any existing subscriptions
        if (this.videoSubscriber) {
            this.videoSubscriber.unsubscribe();
            this.videoSubscriber = null;
        }
    }

    /**
     * Retry API initialization with exponential backoff
     */
    private retryApiInitialization(): void {
        if (this.isDestroyed || this.apiRetryAttempts >= this.maxApiRetryAttempts) {
            if (!this.isDestroyed) {
                this.handleApiInitializationFailure(new Error("Max retry attempts reached"));
            }
            return;
        }

        this.apiRetryAttempts++;
        const delay = Math.min(1000 * Math.pow(2, this.apiRetryAttempts), 10000);

        console.log(`Retrying API initialization in ${delay}ms (attempt ${this.apiRetryAttempts})`);

        this.initializationTimeout = setTimeout(() => {
            if (!this.isDestroyed && this.vgAPI) {
                this.onPlayerReady(this.vgAPI);
            }
        }, delay);
    }

    // Add this method to handle volume changes
    private setupVolumeSubscription(): void {
        if (this.isDestroyed || !this.vgAPI?.subscriptions) return;

        try {
            // Subscribe to volume changes
            if (this.vgAPI.subscriptions.volumeChange) {
                this.vgAPI.subscriptions.volumeChange
                    .pipe(
                        catchError((error) => {
                            console.error("Error in volumeChange subscription:", error);
                            return of(null);
                        }),
                        takeUntil(this.destroy$),
                    )
                    .subscribe(() => {
                        if (!this.isDestroyed) {
                            this.syncAudioVolume();
                        }
                    });
            }

            // Subscribe to mute changes
            if (this.vgAPI.subscriptions.muteChange) {
                this.vgAPI.subscriptions.muteChange
                    .pipe(
                        catchError((error) => {
                            console.error("Error in muteChange subscription:", error);
                            return of(null);
                        }),
                        takeUntil(this.destroy$),
                    )
                    .subscribe(() => {
                        if (!this.isDestroyed) {
                            this.syncAudioVolume();
                        }
                    });
            }
        } catch (error) {
            console.error("Error setting up volume subscription:", error);
        }
    }

    // Add this method to synchronize audio volume with video volume
    private syncAudioVolume(): void {
        if (this.isDestroyed) return;

        try {
            if (!this.vgAPI || !this.audioPlayer?.nativeElement) {
                return;
            }

            const video = this.videoElement?.nativeElement;
            const audio = this.audioPlayer.nativeElement;

            if (video) {
                // Update audio volume to match video volume
                audio.volume = video.volume;
                audio.muted = video.muted;

                // Store current volume state
                this.currentVolume = video.volume;
                this.isMuted = video.muted;

                console.log(`Volume synced: ${video.volume}, Muted: ${video.muted}`);
            }
        } catch (error) {
            console.error("Error syncing audio volume:", error);
        }
    }

    // Updated onVideoTimeUpdate method
    onVideoTimeUpdate(currentTime: number): void {
        if (this.isDestroyed || typeof currentTime !== "number" || isNaN(currentTime)) return;

        try {
            this.updateProgress();
            const audio = this.audioPlayer?.nativeElement;
            const video = this.videoElement?.nativeElement;

            if (this.audioSource && audio && video) {
                // Sync audio time with video time
                if (Math.abs(audio.currentTime - currentTime) > this.maxAudioGap) {
                    console.log("maxAudioGap: " + this.maxAudioGap);
                    this.maxAudioGap = Math.min(this.maxAudioGap * 2, 2.0); // Cap at 2 seconds
                    console.log("onVideoTimeUpdate: " + currentTime + ", " + audio.currentTime);
                    audio.currentTime = currentTime;

                    if (audio.paused && !video.paused) {
                        console.log("audio.play()");
                        audio.play().catch(error => console.log("Playback error:", error));
                    }
                }

                // Ensure audio volume matches video volume
                if (Math.abs(audio.volume - video.volume) > 0.01 || audio.muted !== video.muted) {
                    audio.volume = video.volume;
                    audio.muted = video.muted;
                }
            }
        } catch (error) {
            console.error("Error in onVideoTimeUpdate:", error);
        }
    }

    // Updated playClick method
    playClick(): void {
        if (this.isDestroyed) return;

        try {
            this.showManual = false;

            // Play the video when playClick is called
            if (this.videoElement?.nativeElement) {
                const video = this.videoElement.nativeElement;
                video.play().catch(error => {
                    console.error("Error playing video in playClick:", error);
                    if (isHostDevOrLocal()) this.toastr.error("Failed to play video. Please try again.");
                });
            }

            // Also play audio if available and sync it
            if (this.audioPlayer?.nativeElement && this.audioSource) {
                const audio = this.audioPlayer.nativeElement;
                const video = this.videoElement?.nativeElement;

                // Set audio volume to match video volume
                if (video) {
                    audio.volume = video.volume;
                    audio.muted = video.muted;
                } else {
                    audio.volume = this.currentVolume;
                    audio.muted = this.isMuted;
                }

                // Sync audio with video time
                if (video) {
                    audio.currentTime = video.currentTime;
                }

                audio.play().catch(error => {
                    console.error("Error playing audio in playClick:", error);
                });
            }

            // Reset audio gap for synchronization
            this.maxAudioGap = 0.1;

        } catch (error) {
            console.error("Error in playClick:", error);
        }
    }

    // Updated onVideoPause method
    onVideoPause(): void {
        if (this.isDestroyed) return;

        try {
            console.log("video pause");
            if (this.audioPlayer?.nativeElement) {
                // Pause audio instead of muting it
                this.audioPlayer.nativeElement.pause();
            }
            this.maxAudioGap = 0.1;
        } catch (error) {
            console.error("Error in onVideoPause:", error);
        }
    }

    // Updated onVideoSeek method
    onVideoSeek(): void {
        if (this.isDestroyed) return;

        try {
            console.log("onVideoSeek");
            if (this.videoInit == null) {
                this.isVideoEnded = false;
                if (this.videoElement?.nativeElement) {
                    this.videoElement.nativeElement.play().catch(error => {
                        console.error("Error playing video on seek:", error);
                    });
                }
                if (this.audioPlayer?.nativeElement) {
                    const video = this.videoElement?.nativeElement;
                    const audio = this.audioPlayer.nativeElement;

                    // Sync volume instead of forcing to 1.0
                    if (video) {
                        audio.volume = video.volume;
                        audio.muted = video.muted;
                        audio.currentTime = video.currentTime;
                    }

                    if (!video?.paused) {
                        audio.play().catch(error => {
                            console.error("Error playing audio on seek:", error);
                        });
                    }
                }
            }
        } catch (error) {
            console.error("Error in onVideoSeek:", error);
        }
    }

    // Add method to manually set volume (optional - for custom controls)
    setVolume(volume: number): void {
        if (this.isDestroyed) return;

        try {
            const video = this.videoElement?.nativeElement;
            const audio = this.audioPlayer?.nativeElement;

            this.currentVolume = Math.max(0, Math.min(1, volume));

            if (video) {
                video.volume = this.currentVolume;
            }

            if (audio) {
                audio.volume = this.currentVolume;
            }
        } catch (error) {
            console.error("Error setting volume:", error);
        }
    }

    // Add method to toggle mute (optional - for custom controls)
    toggleMute(): void {
        if (this.isDestroyed) return;

        try {
            const video = this.videoElement?.nativeElement;
            const audio = this.audioPlayer?.nativeElement;

            this.isMuted = !this.isMuted;

            if (video) {
                video.muted = this.isMuted;
            }

            if (audio) {
                audio.muted = this.isMuted;
            }
        } catch (error) {
            console.error("Error toggling mute:", error);
        }
    }

    /**
     * Generic error handler for consistent error handling throughout the component
     */
    private handleError(message: string, error: any): void {
        if (this.isDestroyed) return;
        console.error(message, error);
        // Only show toastr for critical errors to avoid spam
        if (message.includes("initialization") || message.includes("Failed to")) {
            if (isHostDevOrLocal()) this.toastr.error(message);
        }
    }

    override destroy(): void {
        try {
            this.isDestroyed = true;
            super.destroy();
            this.clearVideo();
            this.saveStorageProgress();

            if (this.videoSubscriber) {
                this.videoSubscriber.unsubscribe();
                this.videoSubscriber = null;
            }

            // Clear timeouts
            if (this.clickTimeout) {
                clearTimeout(this.clickTimeout);
                this.clickTimeout = null;
            }

            if (this.initializationTimeout) {
                clearTimeout(this.initializationTimeout);
                this.initializationTimeout = null;
            }

        } catch (error) {
            console.error("Error in destroy:", error);
        }
    }
}
